import { routes } from '@constants';
import { useNavigateWithOfflineMode, useScreenSize } from '@hooks';
import NoiseImage from '@pngs/Noise5.png';
import { Button, Flex, theme, Typography } from '@styles';
import { bool, func, node, object, string } from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

const AuthModalTemplate = ({
  backgroundImage,
  buttonDisabled,
  buttonText,
  formComponent,
  onMainButtonClick,
  onSubButtonClick,
  onSubtitleButtonClick,
  rightContainerProps,
  subButtonText,
  subButtonTextComponent,
  subtitleText,
  titleText,
}) => {
  const { i18n } = useTranslation();
  const { navigate } = useNavigateWithOfflineMode();
  const { isTabletSM } = useScreenSize();

  const handleSubtitleTextButtonPress = () => {
    onSubtitleButtonClick();
  };

  const handleButtonClick = () => {
    onMainButtonClick();
  };

  const handleSubButtonClick = () => {
    onSubButtonClick();
  };

  const handleTermsAndPrivacyPress = (route) => () => {
    navigate(route, { state: { from: 'auth' } });
  };

  return (
    <Flex flexDirection="row" {...(isTabletSM && { width: '100%' })}>
      <Flex
        backgroundImage={NoiseImage}
        backgroundSize="cover"
        height="100%"
        left={0}
        position="absolute"
        right={0}
        zIndex={2}
      />
      {!isTabletSM && (
        <Flex
          alignItems="flex-end"
          backgroundImage={backgroundImage}
          backgroundRepeat="no-repeat"
          backgroundSize="cover"
          height={618}
          width={357}
        >
          <Flex
            flex={1}
            flexDirection="row"
            justifyContent="space-between"
            pb={3}
            px={2}
            zIndex={3}
          >
            <Button
              height="fit-content"
              onClick={handleTermsAndPrivacyPress(routes.privacyPolicy)}
              variant="textButton"
            >
              <Typography
                color={theme.colors.white[100]}
                variant="MS-semiregular-s14-lh22"
              >
                {i18n.t('AuthForm.PrivacyPolicy')}
              </Typography>
            </Button>
            <Button
              height="fit-content"
              onClick={handleTermsAndPrivacyPress(routes.termsOfService)}
              variant="textButton"
            >
              <Typography
                color={theme.colors.white[100]}
                variant="MS-semiregular-s14-lh22"
              >
                {i18n.t('AuthForm.TermsOfService')}
              </Typography>
            </Button>
          </Flex>
        </Flex>
      )}
      <Flex
        flex={1}
        flexDirection="column"
        justifyContent="center"
        zIndex={3}
        {...rightContainerProps}
      >
        <Flex
          alignItems="center"
          flexDirection="row"
          justifyContent="space-between"
          mb={3}
        >
          <Typography
            color={theme.colors.white[100]}
            mr={56}
            variant="MS-semibold-s22-lh34"
          >
            {titleText}
          </Typography>
          {subtitleText && (
            <Button
              onClick={handleSubtitleTextButtonPress}
              variant="textButton"
              whiteSpace="nowrap"
            >
              <Typography
                color={theme.colors.schoolBusYellow[100]}
                variant="MS-semibold-s18-lh19"
              >
                {subtitleText}
              </Typography>
            </Button>
          )}
        </Flex>
        {formComponent}
        <Button
          disabled={buttonDisabled}
          onClick={handleButtonClick}
          variant="primary"
        >
          <Typography
            color={buttonDisabled ? '#6d6d6d' : theme.colors.black[100]}
            variant="MS-bold-s16-lh25"
          >
            {buttonText}
          </Typography>
        </Button>
        {subButtonTextComponent && subButtonTextComponent}
        {subButtonText && (
          <Button onClick={handleSubButtonClick} variant="textButton">
            <Typography
              color={theme.colors.white}
              mt={3}
              variant="MS-semiregular-s14-lh22"
            >
              {subButtonText}
            </Typography>
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

AuthModalTemplate.defaultProps = {
  onSubButtonClick: () => {},
  onSubtitleButtonClick: () => {},
  rightContainerProps: null,
  subButtonText: null,
  subButtonTextComponent: null,
  subtitleText: null,
};

AuthModalTemplate.propTypes = {
  backgroundImage: node.isRequired,
  buttonDisabled: bool.isRequired,
  buttonText: string.isRequired,
  formComponent: node.isRequired,
  onMainButtonClick: func.isRequired,
  onSubButtonClick: func,
  onSubtitleButtonClick: func,
  rightContainerProps: object,
  subButtonText: string,
  subButtonTextComponent: node,
  subtitleText: string,
  titleText: string.isRequired,
};

export default AuthModalTemplate;
